<template>
  <div class="container">
    <!-- NEW COMMENT -->
    <div class="row">
      <div class="col-1 p-0 pl-3">
        <b-avatar
          size="lg"
          :src="currentAvatar"
        />
      </div>
      <div class="col-8 p-0">
        <div class="form-label-group mt-1">
          <Mentionable
            :keys="['@']"
            :items="items"
            @search="getUsers($event)"
            @apply="applyUser($event)"
          >
            <!-- v-model="newComment" -->
            <b-form-textarea
              class="w-100"
              placeholder="Agrega un comentario público y usa @ para mencionar a alguien"
              v-bind:value="newComment"
              v-on:input="newComment = $event"
              id="comment"
              data-cy="write-comment"
              rows="5"
            />
            <template #no-result>
              {{ loading ? 'Buscando...' : 'Sin coincidencias.' }}
            </template>
          </Mentionable>
          <label for="label-comment">Agrega un comentario público:</label>
        </div>
      </div>
      <div class="col-3 p-2 mt-1">
        <b-button
          v-has-permission="'ED087'"
          class="text-sm p-2 mb-1 w-100"
          variant="primary"
          :disabled="disabledComment || processingSave"
          @click="addComment"
          data-cy="comment"
        >
          Comentar
          <span v-show="processingSave" class="spinner-border spinner-border-sm text-white"/>
        </b-button>
        <b-button
          class="text-sm p-2 w-100"
          variant="outline-danger"
          :disabled="disabledComment || processingSave"
          @click="newComment=''"
          data-cy="cancel"
        >
          Cancelar
        </b-button>
      </div>
    </div>
    <hr/>
    <div v-if="!commentsStack.length && loading === false">
      <h5 class="mt-3 text-center">No hay comentarios para este elemento</h5>
    </div>
    <div v-else class="row justify-content-center">
      <app-timeline class="col-12 col-lg-8">
        <app-timeline-item
          variant="info"
          v-for="(comment, index) in commentsStack"
          :key="`comment_${index}`"
        >
          <b-media v-show="!loading">
            <template #aside>
              <b-avatar :src="comment.avatar"/>
            </template>
            <div class="d-flex flex-sm-row flex-column
                flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <h6>{{ comment.usuario.alias }}</h6>
              <div>
                  <span class="text-xs pr-3 text-secondary">
                    {{
                      $moment(comment.fecha, "YYYY-MM-DD HH:mm:ss").format('LLLL')
                    }}
                  </span>
                <feather-icon
                  icon="EditIcon"
                  size="16"
                  class="text-success cursor-pointer mr-2"
                  v-tooltip="'Editar'"
                  @click="editComment(index)"
                  v-if="comment.usuario.id === currentUser.id"
                />
                <feather-icon
                  icon="Trash2Icon"
                  size="16"
                  class="text-danger cursor-pointer"
                  v-tooltip="'Eliminar'"
                  :disabled="processingDelete"
                  @click="deleteComment(index)"
                  v-if="comment.usuario.id === currentUser.id"
                />
              </div>
            </div>
            <!-- EDIT COMMENT -->
            <div v-if="editingCommentIndex === index">
              <div class="row mt-4">
                <div class="col-12">
                  <div class="form-label-group mt-1">
                    <Mentionable
                      :keys="['@']"
                      :items="items"
                      @search="getUsers($event)"
                      @apply="applyUser($event)"
                    >
                      <b-form-textarea
                        class="w-100 mb-3"
                        placeholder="Agrega un comentario público..."
                        v-model="editingComment"
                        :id="`edit-comment-${index}`"
                        rows="5"
                      />
                      <template #no-result>
                        {{ loading ? 'Buscando...' : 'Sin coincidencias :(' }}
                      </template>
                    </Mentionable>
                    <label :for="`edit-comment-${index}`">
                      Agrega un comentario público:
                    </label>
                  </div>
                </div>
                <div class="col-12">
                  <b-button
                    class="text-sm p-2 w-32 mr-2"
                    variant="primary"
                    :disabled="!editingComment.length || processingUpdate"
                    @click="updateComment(index)"
                  >
                    Guardar
                    <span
                      v-show="processingUpdate"
                      class="spinner-border spinner-border-sm text-white"
                    />
                  </b-button>
                  <b-button
                    class="text-sm p-2 w-32"
                    variant="outline-danger"
                    :disabled="!editingComment.length || processingUpdate"
                    @click="resetEditForm"
                  >
                    Cancelar
                  </b-button>
                </div>
              </div>
            </div>
            <p v-else v-html="formatComment(comment.comentario)"></p>
          </b-media>
          <b-media v-show="loading">
            <template #aside>
              <b-skeleton type="avatar" animation="fade"/>
            </template>
            <div class="d-flex flex-sm-row flex-column
                flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <b-skeleton class="w-64"/>
              <b-skeleton class="w-16"/>
            </div>
            <p>
              <b-skeleton class="w-64"/>
            </p>
          </b-media>
        </app-timeline-item>
      </app-timeline>
    </div>
    <div v-if="loading">
      <div class="row">
        <div class="col-1">
          <b-skeleton animation="fade" type="avatar"/>
        </div>
        <div class="col-9">
          <b-skeleton animation="fade" type="input"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';

import Mentionable from '@/febos/global/_vue/componentes/Mentionable';
import {
  ioEDElementsCommentsList,
  ioEDElementsCommentsAdd,
  ioEDElementsCommentsUpdate,
  ioEDElementsCommentsDelete
} from '@/febos/servicios/api/repositories.api';
import AppTimeline from '@core/components/app-timeline/AppTimeline';
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem';
import actionType from '@/febos/global/documentManagement/mixins/actionType';

export default {
  name: 'CommentsView',
  data() {
    return {
      commentsStack: [],
      disabledComment: true,
      loading: false,
      processingSave: false,
      processingUpdate: false,
      processingDelete: false,
      newComment: '',
      editingComment: '',
      editingCommentIndex: null,
      items: [],
      mentioned: [],
    };
  },
  mixins: [actionType],
  components: {
    AppTimeline,
    AppTimelineItem,
    Mentionable,
  },
  mounted() {
    this.loadFileComments();
  },
  methods: {
    ...mapActions('List', ['fetchUsers']),
    getLabelsFromText(text) {
      // El regex encuentra por ej: <@Gabriel Peralta:14b4cb90-533f-4499-baba-c9f21b13fe4c>
      const regex = /<@.*?(?=:).*?(?=>)>/gi;
      const labels = text.match(regex);
      const mentions = [];
      if (!labels) return mentions;
      labels.forEach((label) => {
        const parts = label.slice(2, -1).split(':');
        mentions.push({
          id: parts[1],
          label,
          value: parts[0],
          html: `<span class="mention">@${parts[0]}</span>`
        });
      });
      return mentions;
    },
    formatComment(comment) {
      const labels = this.getLabelsFromText(comment);
      let formattedComment = comment;
      if (!labels) return formattedComment;
      labels.forEach((label) => {
        formattedComment = formattedComment.replace(label.label, label.html);
      });
      return formattedComment;
    },
    exportComment(comment) {
      const doneMentions = {};
      let exportedComment = comment;
      this.mentioned.forEach((mention) => {
        if (!doneMentions[mention.id]) {
          doneMentions[mention.id] = true;
          const label = `<@${mention.value.trim()}:${mention.id}>`;
          const at = `@${mention.value}`;
          exportedComment = exportedComment.replaceAll(at, label);
        }
      });
      return exportedComment;
    },
    importComment(comment) {
      const labels = this.getLabelsFromText(comment);
      const that = this;
      let importedComment = comment;
      labels.forEach((label) => {
        importedComment = importedComment.replaceAll(label.label, `@${label.value.trim()}`);
        that.mentioned.push(label);
      });
      return importedComment;
    },
    applyUser(user) {
      this.mentioned.push(user);
    },
    getUsers(query) {
      this.loading = true;
      this.debounceFetchUsers({ page: 1, query });
      this.loading = false;
    },
    debounceFetchUsers: _.debounce(async function debounceFetchUsers(payload = {}) {
      await this.fetchUsers(payload);
      this.items = this.usersState.value.map((user) => ({
        id: user.id,
        value: `${user.label}`,
      }));
    }, 0),
    resetComponent() {
      this.commentsStack = [];
      this.disabledComment = true;
      this.loading = false;
      this.processingSave = false;
      this.processingUpdate = false;
      this.processingDelete = false;
      this.newComment = '';
      this.editingComment = '';
      this.editingCommentIndex = null;
    },
    resetEditForm() {
      this.editingCommentIndex = null;
      this.editingComment = '';
    },
    async loadFileComments() {
      this.loading = true;
      const response = await ioEDElementsCommentsList({
        repositoryId: this.getSelectedElement.repositoryId,
        elementId: this.getSelectedElement.id,
        order: encodeURIComponent('-fecha'),
      });
      this.commentsStack = response.data.comentarios;
      this.loading = false;
    },
    async addComment() {
      this.loading = true;
      this.processingSave = true;
      let response = {};
      const payload = {
        repositoryId: this.getSelectedElement.repositoryId,
        comment: this.exportComment(this.newComment),
        elementId: this.getSelectedElement.id,
        type: this.getSelectedElement.type === 'file'
          ? this.NEW_FILE
          : this.NEW_FOLDER
      };
      response = await ioEDElementsCommentsAdd(payload);
      const newComment = {
        comentarioId: response.data.comentario.comentarioId,
        comentario: response.data.comentario.comentario,
        fecha: response.data.comentario.fecha,
        usuario: {
          id: this.currentUser.id,
          alias: this.currentUser.alias,
          avatar: this.currentAvatar
        }
      };
      this.commentsStack.unshift(newComment);
      this.newComment = '';
      this.processingSave = false;
      this.loading = false;
    },
    async editComment(index) {
      this.editingCommentIndex = index;
      this.editingComment = this.importComment(this.commentsStack[index].comentario);
    },
    async updateComment(index) {
      this.processingUpdate = true;
      const payload = {
        repositoryId: this.getSelectedElement.repositoryId,
        elementId: this.getSelectedElement.id,
        commentId: this.commentsStack[index].comentarioId,
        comment: this.exportComment(this.editingComment),
        type: this.getSelectedElement.type === 'file'
          ? this.UPDATED_FILE
          : this.UPDATED_FOLDER
      };
      await ioEDElementsCommentsUpdate(payload);
      await this.loadFileComments();
      this.resetEditForm();
      this.processingUpdate = false;
    },
    async deleteComment(index) {
      this.processingDelete = true;
      await ioEDElementsCommentsDelete({
        repositoryId: this.getSelectedElement.repositoryId,
        elementId: this.getSelectedElement.id,
        commentId: this.commentsStack[index].comentarioId,
        type: this.getSelectedElement.type === 'file'
          ? this.DELETED_FILE
          : this.DELETED_FOLDER
      });
      this.commentsStack.splice(index, 1);
      this.processingDelete = false;
    },
  },
  computed: {
    ...mapGetters('List', ['usersState']),
    ...mapGetters('Usuario', [
      'currentUser'
    ]),
    ...mapGetters('DocManagement', [
      'getSelectedElement'
    ]),
    currentAvatar() {
      return this.currentUser.avatar || require('../../../../assets/images/no-avatar.svg');
    }
  },
  watch: {
    getSelectedElement() {
      this.resetComponent();
      this.loadFileComments();
    },
    newComment() {
      if (!this.newComment.length) {
        this.disabledComment = true;
      } else {
        this.disabledComment = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/mentionable.scss";
</style>
